import React, { ComponentProps, ReactNode } from "react";
import * as Sentry from "@sentry/browser";
import { Map, MapRef } from "react-map-gl/mapbox-legacy";
import { buildLoginURLWithPrevLocation } from "../../utils/params";

export type ViewportConfig = {
  zoom: number;
  latitude: number;
  longitude: number;
};

export type Cursor = "grab" | "crosshair" | "pointer" | "unset";

type Props = {
  setViewport: (config: ViewportConfig) => void;
  children: ReactNode;
  cursor?: Cursor;
  baseMapStyle: string;
  width?: string | number;
  height?: string | number;
} & Omit<ComponentProps<typeof Map>, "mapStyle">;

const SimpleMap = React.forwardRef<MapRef, Props>((props, ref) => {
  const {
    setViewport,
    children,
    cursor,
    baseMapStyle,
    width,
    height,
    style,
    ...mapProps
  } = props;

  return (
    <Map
      cursor={cursor ?? "unset"}
      ref={ref}
      {...mapProps}
      style={{ width, height, ...style }}
      mapboxAccessToken={window.env.MAPBOX_ACCESS_TOKEN}
      attributionControl={false}
      reuseMaps={false}
      mapStyle={baseMapStyle}
      localIdeographFontFamily="sans-serif"
      onError={(e: any) => {
        if (e.error?.status === 401) {
          window.location.href = buildLoginURLWithPrevLocation(location);
        }
      }}
      onMove={evt => {
        const { zoom, latitude, longitude } = evt.viewState;
        const config = { zoom, latitude, longitude };
        try {
          setViewport(config);
        } catch (error) {
          Sentry.withScope(scope => {
            scope.setExtras({ config });
            Sentry.captureException(error);
          });
        }
      }}
    >
      {children}
    </Map>
  );
});

export default SimpleMap;
